import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "../../components/ui/dialog";
import { Plus, Trash2, Tag, Copy, X, Power, PowerOff, Share2, RotateCcw, Search, Trophy } from "lucide-react";
import { toast } from "sonner";
import { fmt } from "../../lib/money";

const blank = {
  code: "", discount_type: "percent", discount_value: "",
  min_subtotal: "", max_uses: "", max_uses_per_customer: "", expires_at: "", active: true,
  scope: "all", product_ids: [],
};

function copyText(t) { try { navigator.clipboard.writeText(t); toast.success("Copied"); } catch { /* ignore */ } }

function shareLink(code) {
  const url = `${window.location.origin}/?promo=${encodeURIComponent(code)}`;
  if (navigator.share) {
    navigator.share({
      title: `Discount code ${code}`,
      text: `Use code ${code} for a discount on my shop.`,
      url,
    }).catch(() => {
      try { navigator.clipboard.writeText(url); toast.success("Share link copied"); } catch { /* ignore */ }
    });
  } else {
    try { navigator.clipboard.writeText(url); toast.success("Share link copied"); } catch { /* ignore */ }
  }
}

export default function SellerDiscounts() {
  const [codes, setCodes] = useState([]);
  const [analytics, setAnalytics] = useState({ codes: [], totals: { codes: 0, uses: 0, revenue: 0, unique_buyers: 0 } });
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [productSearch, setProductSearch] = useState("");

  const load = () => {
    api.get("/seller/discounts").then((r) => setCodes(r.data)).catch(() => {});
    api.get("/seller/discounts/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/seller/products").then((r) => setProducts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const statsByCodeId = analytics.codes.reduce((acc, a) => { acc[a.code_id] = a; return acc; }, {});

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.discount_value) { toast.error("Code and value required"); return; }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        discount_value: form.discount_type === "free_shipping" ? 0 : parseFloat(form.discount_value),
        min_subtotal: form.min_subtotal ? parseFloat(form.min_subtotal) : 0,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        max_uses_per_customer: form.max_uses_per_customer ? parseInt(form.max_uses_per_customer) : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        active: form.active,
        product_ids: form.scope === "specific" ? form.product_ids : [],
      };
      if (form.scope === "specific" && payload.product_ids.length === 0) {
        toast.error("Pick at least one product, or change scope to 'All my items'");
        setSaving(false);
        return;
      }
      if (form.discount_type !== "free_shipping" && !form.discount_value) {
        toast.error("Discount value required");
        setSaving(false);
        return;
      }
      await api.post("/seller/discounts", payload);
      toast.success("Code created");
      setOpen(false);
      setForm(blank);
      setProductSearch("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    await api.put(`/seller/discounts/${c.code_id}`, { active: !c.active });
    toast.success(c.active ? "Disabled" : "Enabled");
    load();
  };

  const del = async (c) => {
    if (!window.confirm(`Delete code ${c.code}?`)) return;
    await api.delete(`/seller/discounts/${c.code_id}`);
    toast.success("Deleted");
    load();
  };

  const resetUses = async (c) => {
    if (!window.confirm(
      `Reset ${c.code}?\n\n` +
      `• Uses will go from ${c.uses || 0} → 0\n` +
      `• Code will re-activate so it can be redeemed up to ${c.max_uses} more times\n` +
      `• Past orders that used this code are NOT affected\n` +
      `• Past stats (revenue, shoppers reached) stay intact`
    )) return;
    try {
      await api.post(`/seller/discounts/${c.code_id}/reset-uses`);
      toast.success(`${c.code} reset — ready for ${c.max_uses} more uses`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  // ---- Raffle mode ----
  const [raffleFor, setRaffleFor] = useState(null);
  const [raffleStatus, setRaffleStatus] = useState(null);
  const [raffleProduct, setRaffleProduct] = useState("");
  const [raffleTarget, setRaffleTarget] = useState(10);
  const [raffleBusy, setRaffleBusy] = useState(false);

  const openRaffle = async (c) => {
    setRaffleFor(c);
    setRaffleStatus(null);
    setRaffleProduct("");
    setRaffleTarget(10);
    try {
      const r = await api.get(`/admin/discounts/${c.code}/raffle`);
      setRaffleStatus(r.data);
      if (r.data.product_id) setRaffleProduct(r.data.product_id);
      if (r.data.target_position) setRaffleTarget(r.data.target_position);
    } catch {
      setRaffleStatus({ is_raffle: false });
    }
  };

  const enableRaffle = async () => {
    if (!raffleProduct) { toast.error("Pick the prize product"); return; }
    setRaffleBusy(true);
    try {
      await api.post(`/admin/discounts/${raffleFor.code}/raffle`, {
        product_id: raffleProduct,
        target_position: Number(raffleTarget),
      });
      toast.success(`${raffleFor.code} is now a raffle — entry #${raffleTarget} wins`);
      setRaffleFor(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to enable raffle");
    } finally {
      setRaffleBusy(false);
    }
  };

  const resetRaffle = async () => {
    if (!window.confirm(`Restart the ${raffleFor.code} raffle? Entries and the current winner are cleared.`)) return;
    setRaffleBusy(true);
    try {
      await api.post(`/admin/discounts/${raffleFor.code}/raffle/reset`);
      toast.success("Raffle restarted");
      setRaffleFor(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setRaffleBusy(false);
    }
  };

  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight break-words">Discount codes</h1>
          <p className="mt-2 text-sm text-neutral-600 break-words">
            Codes apply only to your products in the buyer's cart. Share them on social, WhatsApp, or with returning customers — the share button copies a one-tap link that auto-applies the code on landing.
          </p>
        </div>
        <Button
          data-testid="new-discount-btn"
          onClick={() => { setForm(blank); setProductSearch(""); setOpen(true); }}
          className="rounded-none bg-[#0033FF] hover:bg-[#0022CC] h-11 px-5 shrink-0"
        >
          <Plus className="w-4 h-4 mr-2" /> New code
        </Button>
      </div>

      {/* Analytics summary strip */}
      <div data-testid="promo-analytics-strip" className="grid grid-cols-2 sm:grid-cols-4 border border-black mb-6">
        {[
          { label: "Codes", value: analytics.totals.codes, testid: "stat-codes" },
          { label: "Total uses", value: analytics.totals.uses, testid: "stat-uses" },
          { label: "Revenue", value: fmt(analytics.totals.revenue), testid: "stat-revenue" },
          { label: "Shoppers reached", value: analytics.totals.unique_buyers, testid: "stat-buyers" },
        ].map((s, i) => (
          <div
            key={s.label}
            data-testid={s.testid}
            className={`p-4 sm:p-5 ${i < 3 ? "sm:border-r border-black" : ""} ${i < 2 ? "border-r border-black" : ""} ${i < 2 ? "border-b sm:border-b-0" : ""} border-black`}
          >
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{s.label}</div>
            <div className="font-display font-black text-2xl mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="sm:hidden text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5 font-bold flex items-center gap-1">
        Swipe ← → to see all columns
      </div>
      <div className="border border-neutral-200 overflow-x-auto max-w-full">
        {codes.length === 0 ? (
          <div className="p-10 text-center text-neutral-500">No codes yet. Create one to give your customers a discount.</div>
        ) : (
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                {[
                  { l: "Code" },
                  { l: "Discount" },
                  { l: "Min order" },
                  { l: "Redemptions", t: "Total times the code was used (incl. repeat redemptions by the same shopper)" },
                  { l: "Revenue" },
                  { l: "Unique shoppers", t: "How many DIFFERENT accounts used the code (deduplicated, no repeats)" },
                  { l: "Expires" },
                  { l: "Status" },
                  { l: "" },
                ].map((h) => (
                  <th
                    key={h.l || Math.random()}
                    title={h.t || undefined}
                    className="text-left px-4 py-3 text-xs uppercase tracking-widest text-neutral-500 font-semibold whitespace-nowrap"
                  >
                    {h.l}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => {
                const a = statsByCodeId[c.code_id] || { uses: c.uses, revenue: 0, unique_buyers: 0 };
                return (
                <tr key={c.code_id} data-testid={`disc-row-${c.code_id}`} className="border-b border-neutral-200 last:border-b-0">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Tag className="w-3.5 h-3.5 text-neutral-500" />
                      <code className="font-display font-bold tracking-tight">{c.code}</code>
                      <button onClick={() => copyText(c.code)} className="text-neutral-500 hover:text-black" data-testid={`copy-${c.code_id}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
                      {c.product_ids && c.product_ids.length > 0
                        ? `${c.product_ids.length} item${c.product_ids.length === 1 ? "" : "s"}`
                        : "All items"}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-display font-black">
                    {c.discount_type === "percent" ? `${c.discount_value}%` : c.discount_type === "free_shipping" ? "Free shipping" : c.discount_type === "shipping_percent" ? `${c.discount_value}% off shipping` : c.discount_type === "fixed_plus_free_shipping" ? `${fmt(c.discount_value)} + free shipping` : c.discount_type === "percent_plus_free_shipping" ? `${c.discount_value}% + free shipping` : fmt(c.discount_value)}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">{c.min_subtotal ? fmt(c.min_subtotal) : "—"}</td>
                  <td className="px-4 py-3 text-neutral-600" data-testid={`uses-${c.code_id}`}>
                    {(() => {
                      const exhausted = c.max_uses && (a.uses || 0) >= c.max_uses;
                      const repeats = (a.uses || 0) - (a.unique_buyers || 0);
                      return (
                        <>
                          <span className={exhausted ? "text-[#FF2A00] font-bold" : ""}>
                            {a.uses}{c.max_uses ? ` / ${c.max_uses}` : ""}
                            {exhausted && <span className="ml-1 text-[9px] uppercase tracking-widest">maxed</span>}
                          </span>
                          {repeats > 0 && (
                            <div
                              className="text-[10px] text-neutral-400 mt-0.5"
                              title="Times the same shopper used this code more than once"
                            >
                              ({repeats} repeat{repeats === 1 ? "" : "s"})
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 font-display font-bold" data-testid={`revenue-${c.code_id}`}>
                    {a.revenue > 0 ? fmt(a.revenue) : <span className="text-neutral-400 font-normal">—</span>}
                  </td>
                  <td className="px-4 py-3 text-neutral-600" data-testid={`buyers-${c.code_id}`}>
                    {a.unique_buyers > 0 ? a.unique_buyers : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3">
                    {(() => {
                      const exhausted = c.max_uses && (a.uses || 0) >= c.max_uses;
                      if (exhausted) {
                        return (
                          <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-[#FF2A00] text-white" title="Usage limit reached — code will be rejected at checkout">
                            exhausted
                          </span>
                        );
                      }
                      return (
                        <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 ${c.active ? "bg-[#00A355] text-white" : "bg-neutral-300 text-neutral-700"}`}>
                          {c.active ? "active" : "off"}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <Button
                        data-testid={`raffle-${c.code_id}`}
                        onClick={() => openRaffle(c)}
                        variant="outline" size="icon"
                        className={`rounded-none h-8 w-8 ${c.raffle ? "border-[#0033FF] text-[#0033FF] bg-[#0033FF]/10" : "border-black hover:bg-[#0033FF] hover:text-white hover:border-[#0033FF]"}`}
                        title={c.raffle ? "Raffle mode ON — manage" : "Turn into a raffle (Nth entry wins the item free)"}
                      >
                        <Trophy className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        data-testid={`share-${c.code_id}`}
                        onClick={() => shareLink(c.code)}
                        variant="outline" size="icon"
                        className="rounded-none border-black h-8 w-8 hover:bg-[#0033FF] hover:text-white hover:border-[#0033FF]"
                        title="Share one-tap link"
                      >
                        <Share2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        data-testid={`toggle-${c.code_id}`}
                        onClick={() => toggleActive(c)}
                        variant="outline" size="icon"
                        className="rounded-none border-black h-8 w-8"
                        title={c.active ? "Disable" : "Enable"}
                      >
                        {c.active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                      </Button>
                      {c.max_uses && (a.uses || 0) >= c.max_uses && (
                        <Button
                          data-testid={`reset-uses-${c.code_id}`}
                          onClick={() => resetUses(c)}
                          variant="outline" size="icon"
                          className="rounded-none border-[#00A355] text-[#00A355] hover:bg-[#00A355] hover:text-white h-8 w-8"
                          title="Reset uses to 0 (re-enables the code for another batch of redemptions)"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button
                        data-testid={`delete-${c.code_id}`}
                        onClick={() => del(c)}
                        variant="outline" size="icon"
                        className="rounded-none border-black h-8 w-8 hover:bg-[#FF2A00] hover:text-white hover:border-[#FF2A00]"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!raffleFor} onOpenChange={(v) => !v && setRaffleFor(null)}>
        <DialogContent className="rounded-none border-black w-screen sm:w-[calc(100%-2rem)] max-w-md h-[100dvh] sm:h-auto p-0 gap-0 flex flex-col top-0 left-0 translate-x-0 translate-y-0 sm:top-[50%] sm:left-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%]">
          <DialogHeader className="sticky top-0 z-10 bg-white border-b border-neutral-200 px-4 sm:px-6 py-3 flex-row items-center justify-between space-y-0">
            <div className="text-left">
              <DialogTitle className="font-display font-black text-xl sm:text-2xl flex items-center gap-2">
                <Trophy className="w-5 h-5 text-[#0033FF]" /> Raffle — {raffleFor?.code}
              </DialogTitle>
              <DialogDescription className="text-xs text-neutral-500 hidden sm:block">
                Entries 1 to N−1 get “sorry, not this time”. Entry #N wins the item free + free shipping. After that, everyone sees “someone has already won”.
              </DialogDescription>
            </div>
            <button type="button" onClick={() => setRaffleFor(null)} className="w-10 h-10 grid place-items-center border border-black hover:bg-black hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </DialogHeader>
          <div className="px-4 sm:px-6 py-4 space-y-4">
            {!raffleStatus ? (
              <div className="text-sm text-neutral-500">Loading…</div>
            ) : (
              <>
                {raffleStatus.is_raffle && (
                  <div className="border border-[#0033FF] bg-[#0033FF]/5 p-3 text-sm" data-testid="raffle-live-status">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#0033FF] mb-1">Raffle is ON</div>
                    <div className="font-display font-black text-2xl">
                      {raffleStatus.participants_count}<span className="text-sm text-neutral-400 font-bold"> / {raffleStatus.target_position} entries</span>
                    </div>
                    {raffleStatus.winner && (
                      <div className="mt-2 text-sm">
                        <span className="font-semibold text-[#00A355]">Won by {raffleStatus.winner.name || raffleStatus.winner.email}</span>
                        {raffleStatus.winner.whatsapp && <span className="text-neutral-600"> — {raffleStatus.winner.whatsapp}</span>}
                      </div>
                    )}
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="raffle-product">Prize product</Label>
                  <Select value={raffleProduct} onValueChange={setRaffleProduct}>
                    <SelectTrigger id="raffle-product" data-testid="raffle-product-select" className="rounded-none border-black">
                      <SelectValue placeholder="Pick the item the winner gets free" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((p) => (
                        <SelectItem key={p.product_id} value={p.product_id} data-testid={`raffle-product-opt-${p.product_id}`}>
                          {p.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="raffle-target">Winning entry #</Label>
                  <Input
                    id="raffle-target" data-testid="raffle-target-input"
                    type="number" min={2} max={10000}
                    value={raffleTarget}
                    onChange={(e) => setRaffleTarget(e.target.value)}
                    className="rounded-none border-black"
                  />
                  <p className="text-xs text-neutral-500">The 10th person to enter the code wins (set any number from 2 up).</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid="raffle-enable-btn"
                    onClick={enableRaffle}
                    disabled={raffleBusy || !raffleProduct}
                    className="rounded-none bg-[#0033FF] hover:bg-[#0022CC] h-11 px-5"
                  >
                    {raffleBusy ? "Saving…" : raffleStatus.is_raffle ? "Save & restart raffle" : "Turn on raffle mode"}
                  </Button>
                  {raffleStatus.is_raffle && (
                    <Button
                      data-testid="raffle-reset-btn"
                      onClick={resetRaffle}
                      disabled={raffleBusy}
                      variant="outline"
                      className="rounded-none border-black h-11 px-5"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" /> Restart
                    </Button>
                  )}
                </div>
                {raffleStatus.is_raffle && (
                  <p className="text-xs text-neutral-500">Saving or restarting clears current entries and the winner, so the raffle starts fresh.</p>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-black w-screen sm:w-[calc(100%-2rem)] max-w-lg h-[100dvh] sm:h-auto sm:max-h-[88vh] p-0 gap-0 flex flex-col top-0 left-0 translate-x-0 translate-y-0 sm:top-[50%] sm:left-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%]">
          <DialogHeader className="sticky top-0 z-10 bg-white border-b border-neutral-200 px-4 sm:px-6 py-3 flex-row items-center justify-between space-y-0">
            <div className="text-left">
              <DialogTitle className="font-display font-black text-xl sm:text-2xl">New discount code</DialogTitle>
              <DialogDescription className="text-xs text-neutral-500 hidden sm:block">
                Applies only to your products in the buyer's cart.
              </DialogDescription>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="w-10 h-10 grid place-items-center border border-black hover:bg-black hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </DialogHeader>
          <form id="disc-form" onSubmit={submit} className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="code">Code</Label>
              <Input
                id="code" data-testid="disc-code-input"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="e.g. SUMMER10"
                required
                className="rounded-none border-black h-11 font-mono tracking-wider"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v, discount_value: v === "free_shipping" ? "0" : form.discount_value })}>
                  <SelectTrigger data-testid="disc-type" className="rounded-none border-black h-11"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none border-black">
                    <SelectItem value="percent">% Percent off items</SelectItem>
                    <SelectItem value="fixed">Fixed amount off</SelectItem>
                    <SelectItem value="percent_plus_free_shipping">% Off + free shipping</SelectItem>
                    <SelectItem value="fixed_plus_free_shipping">Fixed amount off + free shipping</SelectItem>
                    <SelectItem value="free_shipping">Free shipping</SelectItem>
                    <SelectItem value="shipping_percent">% Off shipping</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="value">
                  Value {form.discount_type === "percent" || form.discount_type === "shipping_percent" || form.discount_type === "percent_plus_free_shipping" ? "(%)" : form.discount_type === "fixed" || form.discount_type === "fixed_plus_free_shipping" ? "(G$)" : ""}
                </Label>
                <Input
                  id="value" data-testid="disc-value-input"
                  type="number" step="0.01" min="0"
                  max={form.discount_type === "percent" || form.discount_type === "shipping_percent" || form.discount_type === "percent_plus_free_shipping" ? "100" : undefined}
                  required={form.discount_type !== "free_shipping"}
                  disabled={form.discount_type === "free_shipping"}
                  value={form.discount_type === "free_shipping" ? "" : form.discount_value}
                  onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                  placeholder={form.discount_type === "free_shipping" ? "Waives shipping" : form.discount_type === "shipping_percent" ? "e.g. 50" : form.discount_type === "fixed_plus_free_shipping" ? "Amount off + shipping waived" : form.discount_type === "percent_plus_free_shipping" ? "e.g. 15 (% off + shipping waived)" : ""}
                  className="rounded-none border-black h-11 disabled:bg-neutral-100 disabled:text-neutral-400"
                />
              </div>
            </div>

            {/* Sell-price helper (only for item-level discounts) */}
            {(form.discount_type === "percent" || form.discount_type === "fixed" || form.discount_type === "percent_plus_free_shipping" || form.discount_type === "fixed_plus_free_shipping") && form.discount_value && (
              <DiscountSellPriceHelper
                discountType={form.discount_type.startsWith("percent") ? "percent" : "fixed"}
                discountValue={parseFloat(form.discount_value) || 0}
              />
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="min">Min subtotal (G$)</Label>
                <Input id="min" type="number" step="0.01" min="0"
                  value={form.min_subtotal}
                  onChange={(e) => setForm({ ...form, min_subtotal: e.target.value })}
                  placeholder="0"
                  className="rounded-none border-black h-11"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="max">Usage limit (total redemptions)</Label>
                <Input id="max" data-testid="disc-max-uses-input" type="number" min="1"
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  placeholder="Unlimited"
                  className="rounded-none border-black h-11"
                />
                <p className="text-[11px] text-neutral-500 leading-snug">
                  How many shoppers can use this code in total? E.g. <b>2</b> means the first two buyers get the discount — after that the code stops working. Leave blank for unlimited.
                </p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="maxpc">Per-shopper limit</Label>
              <Input id="maxpc" data-testid="disc-max-per-customer-input" type="number" min="1"
                value={form.max_uses_per_customer}
                onChange={(e) => setForm({ ...form, max_uses_per_customer: e.target.value })}
                placeholder="Unlimited"
                className="rounded-none border-black h-11"
              />
              <p className="text-[11px] text-neutral-500 leading-snug">
                How many times can the SAME shopper use this code? E.g. <b>1</b> means one redemption per account — they can't keep re-applying it on new orders. Leave blank for no per-shopper limit.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="exp">Expires (optional)</Label>
              <Input id="exp" type="datetime-local"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                className="rounded-none border-black h-11"
              />
            </div>

            {/* Scope: all items vs specific products */}
            <div className="space-y-2 pt-2 border-t border-neutral-200">
              <Label>Applies to</Label>
              <div className="flex gap-2">
                <button
                  data-testid="disc-scope-all"
                  type="button"
                  onClick={() => setForm({ ...form, scope: "all", product_ids: [] })}
                  className={`flex-1 h-11 border text-sm font-semibold transition-colors ${
                    form.scope === "all"
                      ? "bg-black text-white border-black"
                      : "bg-white text-black border-black hover:bg-neutral-100"
                  }`}
                >
                  All my items
                </button>
                <button
                  data-testid="disc-scope-specific"
                  type="button"
                  onClick={() => setForm({ ...form, scope: "specific" })}
                  className={`flex-1 h-11 border text-sm font-semibold transition-colors ${
                    form.scope === "specific"
                      ? "bg-black text-white border-black"
                      : "bg-white text-black border-black hover:bg-neutral-100"
                  }`}
                >
                  Specific products
                </button>
              </div>
              {form.scope === "specific" && (() => {
                const q = productSearch.trim().toLowerCase();
                const filtered = q
                  ? products.filter((p) => p.title.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q))
                  : products;
                const selectedShownCount = filtered.filter((p) => form.product_ids.includes(p.product_id)).length;
                return (
                  <div className="border border-neutral-300" data-testid="disc-product-picker">
                    <div className="sticky top-0 bg-white border-b border-neutral-200 p-2 flex items-center gap-2">
                      <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                        <input
                          data-testid="disc-product-search"
                          type="text"
                          value={productSearch}
                          onChange={(e) => setProductSearch(e.target.value)}
                          placeholder="Search your items by name or category…"
                          className="w-full h-10 pl-9 pr-9 border border-black rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-[#0033FF]"
                          autoFocus={false}
                        />
                        {productSearch && (
                          <button
                            type="button"
                            onClick={() => setProductSearch("")}
                            data-testid="disc-product-search-clear"
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-neutral-100"
                            aria-label="Clear search"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="text-[11px] uppercase tracking-widest text-neutral-500 whitespace-nowrap">
                        {filtered.length} / {products.length}
                      </div>
                    </div>
                    <div className="max-h-72 sm:max-h-96 overflow-y-auto" data-testid="disc-product-list">
                      {products.length === 0 ? (
                        <div className="p-4 text-sm text-neutral-500">You have no products yet. Add a product first.</div>
                      ) : filtered.length === 0 ? (
                        <div className="p-4 text-sm text-neutral-500">No items match &ldquo;{productSearch}&rdquo;.</div>
                      ) : (
                        filtered.map((p) => {
                          const checked = form.product_ids.includes(p.product_id);
                          return (
                            <label
                              key={p.product_id}
                              className={`flex items-center gap-3 px-3 py-2 border-b border-neutral-200 last:border-b-0 cursor-pointer hover:bg-neutral-50 ${checked ? "bg-neutral-100" : ""}`}
                              data-testid={`disc-product-${p.product_id}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...form.product_ids, p.product_id]
                                    : form.product_ids.filter((id) => id !== p.product_id);
                                  setForm({ ...form, product_ids: next });
                                }}
                                className="w-4 h-4 accent-[#0033FF]"
                              />
                              <div className="w-10 h-10 bg-neutral-100 border border-neutral-200 shrink-0 overflow-hidden">
                                {p.images?.[0] && <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-display font-bold text-sm truncate">{p.title}</div>
                                <div className="text-xs text-neutral-500">{fmt(p.price)} · Stock {p.stock}</div>
                              </div>
                            </label>
                          );
                        })
                      )}
                    </div>
                    {q && selectedShownCount < form.product_ids.length && (
                      <div className="px-3 py-2 border-t border-neutral-200 bg-neutral-50 text-[11px] uppercase tracking-widest text-neutral-600">
                        {form.product_ids.length - selectedShownCount} selected item(s) hidden by search
                      </div>
                    )}
                  </div>
                );
              })()}
              <p className="text-xs text-neutral-500">
                {form.scope === "all"
                  ? "Code will discount any cart line from your shop."
                  : `Code will only discount the ${form.product_ids.length} selected item${form.product_ids.length === 1 ? "" : "s"}.`}
              </p>
            </div>
          </form>
          <div className="sticky bottom-0 z-10 bg-white border-t border-neutral-200 px-4 sm:px-6 py-3 flex gap-2 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))]">
            <Button type="button" onClick={() => setOpen(false)} variant="outline" className="flex-1 sm:flex-none rounded-none border-black h-12">Cancel</Button>
            <Button data-testid="disc-save-btn" type="submit" form="disc-form" disabled={saving} className="flex-1 rounded-none bg-[#0033FF] hover:bg-[#0022CC] h-12 font-semibold">
              {saving ? "Saving…" : "Create code"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// -------------------- Sell-price helper for new discount codes --------------------

function DiscountSellPriceHelper({ discountType, discountValue }) {
  const [net, setNet] = useState("");
  const target = parseFloat(net);

  let suggested = null;
  let explanation = null;

  if (Number.isFinite(target) && target > 0 && discountValue > 0) {
    if (discountType === "percent") {
      const pct = Math.min(99.99, Math.max(0, discountValue));
      const listPrice = target / (1 - pct / 100);
      suggested = {
        list: listPrice,
        discount: listPrice - target,
      };
      explanation = `Net ÷ (1 − ${pct}%)`;
    } else if (discountType === "fixed") {
      const listPrice = target + discountValue;
      suggested = {
        list: listPrice,
        discount: discountValue,
      };
      explanation = `Net + G$${Math.round(discountValue)} discount`;
    }
  }

  return (
    <div
      data-testid="disc-sell-price-helper"
      className="border border-[#0033FF] bg-[#F0F4FF] p-3"
    >
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#0033FF] mb-1.5 flex items-center gap-1.5">
        <Tag className="w-3 h-3" />
        Sell-price helper — don't undercut yourself
      </div>
      <p className="text-[11px] text-neutral-600 mb-2 leading-snug">
        How much do you want to <b>keep</b> after this discount is used?
        We'll tell you what to list the product at.
      </p>
      <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-neutral-500">
            G$
          </span>
          <Input
            data-testid="disc-helper-net"
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            value={net}
            onChange={(e) => setNet(e.target.value)}
            placeholder="e.g. 8000"
            className="rounded-none border-black h-10 pl-10 font-mono text-sm"
          />
        </div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-700">
          you keep
        </div>
      </div>

      {suggested ? (
        <div
          data-testid="disc-helper-result"
          className="mt-3 border-t border-[#0033FF]/30 pt-3 grid grid-cols-3 gap-2 text-[11px]"
        >
          <div>
            <div className="text-[9px] uppercase tracking-widest font-bold text-neutral-500">
              List at
            </div>
            <div
              data-testid="disc-helper-list"
              className="font-display font-black text-base sm:text-lg text-[#0033FF]"
            >
              {fmt(suggested.list)}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest font-bold text-neutral-500">
              Discount
            </div>
            <div className="font-display font-bold text-sm text-[#FF9500]">
              −{fmt(suggested.discount)}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest font-bold text-neutral-500">
              You keep
            </div>
            <div className="font-display font-bold text-sm text-[#00A355]">
              {fmt(target)}
            </div>
          </div>
          <div className="col-span-3 text-[10px] text-neutral-500 italic">
            Formula: {explanation}
          </div>
        </div>
      ) : (
        net && (
          <div className="mt-2 text-[11px] text-neutral-500">
            Enter a positive amount and set a valid discount value above.
          </div>
        )
      )}
    </div>
  );
}
